import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  PictureAsPdf,
  Description as ExcelIcon,
  Assessment as CsvIcon,
  FilterList,
  Close,
  Refresh,
  FilterAlt,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import { saveFile } from "../../../../utils/common";
import useInventory from "../../../../hooks/useInventory";
import ToolBar from "../../../../components/Toolbar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Inventory</Typography>
      <Typography>Material Stock</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

function MaterialStock() {
  const { getAllMaterialStock, exportInventoryReport, isLoading } = useInventory();
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterMaterialName, setFilterMaterialName] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);

  const handleApplyFilters = () => {
    const activeFilters = [];
    if (filterMaterialName.trim()) {
      activeFilters.push({
        fieldName: "materialName",
        fieldValue: filterMaterialName.trim(),
        op: "like",
        dataType: "string",
        condition: "and",
        isJoin: false,
      });
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters(activeFilters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilterMaterialName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters([]);
    setFilterOpen(false);
  };

  const handleDownload = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const resBlob = await exportInventoryReport("material-stock", fileType, {
        filters: appliedFilters,
      });
      await saveFile(
        resBlob,
        `material_stock_${new Date().toISOString().slice(0, 10)}.${fileType}`,
      );
      dispatch(showSuccessSnackbar(`Exported as ${fileType.toUpperCase()} successfully!`));
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to export"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const fetchStockData = async () => {
    try {
      const payload = {
        pagination: {
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
        },
        filters: appliedFilters,
        sortFields: [],
      };
      const res = await getAllMaterialStock(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchStockData();
  }, [paginationModel, appliedFilters]);

  const columns = [
    {
      field: "materialName",
      headerName: "Material Name",
      width: 250,
    },
    {
      field: "currentQuantity",
      headerName: "Current Quantity",
      width: 180,
      type: "number",
    },
    {
      field: "minimumQuantity",
      headerName: "Minimum Quantity",
      width: 180,
      type: "number",
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      width: 220,
      valueFormatter: (value) => {
        if (!value) return "";
        return value.replace("T", " ").substring(0, 19);
      },
    },
  ];

  return (
    <Box>
      <Card mb={6}>
        <CardHeader
          title="Material Stock"
          subheader={breadCrump()}
          action={
            <Box display="flex" gap={1} alignItems="center" mt={1} mr={1}>
              <ToolBar
                refreshPage={fetchStockData}
                openFilterDialog={() => setFilterOpen(true)}
                downloadFile={handleDownload}
                isLoadingDownload={isDownloading}
                downloadFileType={downloadType}
              />
            </Box>
          }
        />
        <CardContent>
          <Paper elevation={0}>
            <div style={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.stockId}
                paginationMode="server"
                rowCount={rowCount}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                loading={isLoading}
                disableRowSelectionOnClick
              />
            </div>
          </Paper>
        </CardContent>
      </Card>

      {/* Filters Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Box display="flex" flexDirection="column" gap={4}>
            <TextField
              label="Material Name"
              variant="outlined"
              value={filterMaterialName}
              onChange={(e) => setFilterMaterialName(e.target.value)}
              placeholder="e.g. Fiber, ONU"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClearFilters} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MaterialStock;
